import { useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";

import OffcanvasTemplate from "../../../../../components/offcanvasTemplate";
import { fetchFtmDocumentsTypes, updateFtmSkillApprove } from "../../../../approvalProcess/service";
import DivSpinner from "../../../../../components/divSpinner";
import { theme } from "../../../../../theme";
import DocumentsFiles from "./DocumentsFiles";
import { commonSwal } from "../../../../../components/swalAlert/swalAlert";
import { Context } from "../../../Provider";

const DocumentsTypes = ({ show, handleClose, ftmId, skill, subSkill }) => {
    const { state, dispatch } = useContext(Context);
    const { ftm } = state;
    const { ftm_id } = ftm;

    const [loading, setLoading] = useState(true);
    const [documentTypes, setDocumentTypes] = useState(null);
    const [showDocumentsFiles, setShowDocumentsFiles] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);

    const handelShowDocumentsFiles = () => setShowDocumentsFiles(true);
    const handelCloseDocumentsFiles = () => setShowDocumentsFiles(false);
    const handleRefresh = () => setRefreshCount((prev) => prev + 1);

    const getDocumentTypes = useCallback(async () => {
        if (!subSkill?.sub_skill_id) return;
        try {
            setLoading(true);
            const response = await fetchFtmDocumentsTypes(ftmId, subSkill.sub_skill_id);
            if (response?.status === "Success") {
                setDocumentTypes(response.data || null);
            }
        } catch (error) {
            console.error("Error fetching document types:", error);
        } finally {
            setLoading(false);
        }
    }, [ftmId, subSkill?.sub_skill_id]);

    useEffect(() => {
        if (show && subSkill) getDocumentTypes();
    }, [show, getDocumentTypes, subSkill, refreshCount]);

    // Loading spinner vertically & horizontally centered
    const LoadingContent = (
        <div
            style={{
                height: `${window.innerHeight - 200}px`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <DivSpinner />
        </div>
    );

    const handelSelectDocumentType = (type, is_expired_document) => {
        if(is_expired_document){
            commonSwal({
                notificationType: "OPEN_NEW_DOCUMENT_TYPE",
                isConfirmed: async () => {
                    setSelectedDocumentType({
                        ...type,
                        is_get_new_document_files: false
                    });
                    handelShowDocumentsFiles();
                },
                isDenied: async () => {
                    setSelectedDocumentType({
                        ...type,
                        is_get_new_document_files: true
                    });
                    handelShowDocumentsFiles();
                },
            });
        }else{
            setSelectedDocumentType({
                ...type,
                is_get_new_document_files: false
            });
            handelShowDocumentsFiles();
        }
    }

    const handleApproveSkill = (ftm_skill_id) => {
        commonSwal({
            notificationType: 'APPROVE_FTM_SKILL_TYPE',
            preConfirm: async () => {
                const response = await updateFtmSkillApprove(ftm_skill_id, ftm_id);
                return response;
            },
            isConfirmed: (result) => {
                handleClose();
                toast.success(result?.value?.message);

                dispatch({ type: "SET_FTM_EDIT_COUNT" });
            }
        })
    }

    const DocumentList = ({ title, documents, type, description }) => (
        <div className="col-md-12 my-4">
            <div className="card" style={{ boxShadow: "unset" }}>
                <div className="card-body">
                    <h6 className="mb-0">{title}</h6>
                    <p className="text-sm">{description}</p>

                    {documents?.length ? (
                        documents.map((doc) => {
                            const {
                                documents_types_id,
                                documents_types_name,
                                documents_types_is_required,
                                is_have_files,
                                is_approve_document,
                                is_expired_document,
                                is_have_new_document,
                                document,
                            } = doc;

                            const expiryDate = document?.expiry_date
                                    ? new Date(document.expiry_date).toLocaleDateString()
                                    : "N/A";

                            const comment = document?.comment;

                            return (
                                <div
                                    key={documents_types_id}
                                    className="d-flex flex-column p-2 text-dark rounded hover-row mb-1"
                                    onClick={() => handelSelectDocumentType(doc, is_expired_document)}
                                >
                                    {/* Top Row */}
                                    <div className="d-flex justify-content-between align-items-center">
                                        {/* Left side */}
                                        <div className="d-flex align-items-center">
                                            <i className={`material-icons my-auto ${is_have_new_document ? "text-warning" : ""}`}>
                                                {is_have_new_document ? "assignment_late" : "document_scanner"}
                                            </i>
                                            <span className="ps-3">{documents_types_name}</span>

                                            {documents_types_is_required === 1 && (
                                                <i className="material-icons text-md opacity-10 text-danger ms-2">
                                                    star
                                                </i>
                                            )}

                                            {is_expired_document && (
                                                <Tooltip title={`Some documents validity has ended.`} arrow placement="right">
                                                    <i className="material-icons text-md opacity-10 text-danger ms-2">
                                                        report_off
                                                    </i>
                                                </Tooltip>
                                            )}

                                            {is_have_files && (
                                                <i
                                                    className={`material-icons text-md opacity-10 text-${theme.main} ms-2`}
                                                >
                                                    library_books
                                                </i>
                                            )}

                                            {comment && (
                                                <Tooltip
                                                    title={comment}
                                                    arrow
                                                    placement="right"
                                                >
                                                    <i
                                                        className={`material-icons text-md opacity-10 text-${theme.main} ms-2`}
                                                    >
                                                        chat_bubble
                                                    </i>
                                                </Tooltip>
                                            )}
                                        </div>

                                        {/* Right side icon */}
                                        <i className={`material-icons cursor-pointer ${is_approve_document && `text-${theme.main}` }`} >
                                            {is_approve_document ? "verified" : "add_circle"}
                                        </i>
                                    </div>

                                    {/* Expiry Date Row */}
                                    <div className="d-flex align-items-center ps-0 mt-1 text-xs text-secondary">
                                        <strong className="me-1">Expiry Date:</strong>
                                        <span>{expiryDate}</span>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="d-flex justify-content-lg-start justify-content-center p-2 text-dark">
                            <i className="material-icons my-auto">remove</i>
                            <span className="ps-3">No document types found.</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <OffcanvasTemplate
            open={show}
            handleClose={handleClose}
            placement="end"
            title="Certificates"
            body={
                <section>
                    {loading ? (
                        LoadingContent
                    ) : (
                        <>
                            <div className="card mb-2" style={{ boxShadow: "unset" }}>
                                <div className="p-2 ps-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <p className="text-sm mb-0 text-capitalize">
                                                {skill?.skill_name}
                                            </p>
                                            <h6 className="mb-0 d-flex align-items-center">
                                                {subSkill?.sub_skill_name}
                                                {subSkill?.skill_is_approve === 1 ? (
                                                    <i className={`material-icons text-${theme.main} ms-2`}>
                                                        verified
                                                    </i>
                                                ) : null}
                                            </h6>
                                        </div>
                                        <div
                                            className={`icon icon-md icon-shape bg-gradient-${theme.main} text-center border-radius-lg`}
                                        >
                                            <i className="material-icons opacity-10">
                                                {documentTypes?.skill_web_app_icon}
                                            </i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <DocumentList
                                title={`${skill?.skill_name} documents types`}
                                documents={documentTypes?.skill_document_types}
                                type="parent"
                                description={`List of document types required or related to each parent skill.`}
                            />

                            <DocumentList
                                title={`${subSkill?.sub_skill_name} documents types`}
                                documents={documentTypes?.sub_skill_document_types}
                                type="sub"
                                description="List of document types required or related to each sub skill."
                            />
                        </>
                    )}

                    <DocumentsFiles 
                        show={showDocumentsFiles}
                        handleClose={handelCloseDocumentsFiles}
                        ftm_id={ftmId}
                        item={selectedDocumentType}
                        handleDocumentListRefresh={handleRefresh}
                    />
                </section>
            }
            footer={
                <div className="container">
                    <div className="row" style={{ marginTop: '10px' }}>
                        <div className="col">
                            <div className="d-grid gap-2 mb-3" >
                                <button
                                    type="button"
                                    className={`btn bg-gradient-${theme.main} btn-sm`}
                                    onClick={() => handleApproveSkill(documentTypes?.ftm_skill?.id)}
                                    disabled={documentTypes?.ftm_skill?.is_approve}
                                >
                                    <i className="material-icons text-lg me-2">
                                        {documentTypes?.skill_web_app_icon}
                                    </i>
                                    Approve {documentTypes?.sub_skill?.sub_skill_name}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            }
        />
    );
};

export default DocumentsTypes;
